import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppContextProvider } from '@contexts/AppContext';
import { SubscribeFormProvider } from '@contexts/SubscribeFormContext';
import useEventTracking from '@hooks/useEventTracking';
import useWindowSize from '@hooks/useWindowSize';
import { ClickTarget, click } from '@tracking';
import { User } from '@utils/backend-api/currentUser';
import { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';
import { WIDTH_SM } from '@utils/constants';
import { SubscriptionStatus } from '@utils/interfaces';

import MobileMultipleOffers from './MobileMultiOffers';
import MultipleOffers from './MultipleOffers';
import SingleOffer from './SingleOffer';

const queryClient = new QueryClient();

export interface SubscribeProps {
  billingAmountCBPT?: number;
  cbptTaxAmountExclusive?: number;
  cbptTotalWithTax?: number;
  defaultSelectedOffer: SelectedOffer;
  description?: string;
  dob: string;
  initialCurrentUser?: User;
  isDueInFuture: boolean;
  isGdprRequired: boolean;
  isYPEligible: boolean;
  offers: OfferOption[];
  stripeCustomerId?: string;
  subscriptionStatus?: SubscriptionStatus;
  userDobVerified?: boolean;
  userEmail?: string;
  userFullName?: string;
  userId?: number;
  userNextBillingDate?: string;
  returnTo?: string; // NEW — "Back to what you were reading" link
}

const Subscribe = ({
  billingAmountCBPT,
  cbptTaxAmountExclusive,
  cbptTotalWithTax,
  defaultSelectedOffer,
  description,
  initialCurrentUser,
  isDueInFuture,
  isGdprRequired,
  isYPEligible,
  offers,
  returnTo,
  stripeCustomerId,
  subscriptionStatus,
  userDobVerified,
  userEmail,
  userFullName,
  userId,
  userNextBillingDate,
}: SubscribeProps) => {
  const { trackEvent } = useEventTracking();
  const isSingleOffer = offers.length === 1;
  const { width: windowWidth } = useWindowSize();
  const isMobileExperience = windowWidth < WIDTH_SM && !!userEmail;

  return (
    <AppContextProvider initialCurrentUser={initialCurrentUser} isGdprRequired={isGdprRequired}>
      <SubscribeFormProvider
        user={{
          billingAmountCBPT,
          cbptTaxAmountExclusive,
          cbptTotalWithTax,
          dobVerified: userDobVerified,
          email: userEmail,
          id: userId,
          isDueInFuture,
          isYPEligible,
          name: userFullName,
          nextBillingDate: userNextBillingDate,
          stripeCustomerId,
          subscriptionStatus,
        }}
      >
        <QueryClientProvider client={queryClient}>
          {returnTo && (
            <div className="mb-24 text-center">
              <a
                href={returnTo}
                className="text-sm text-grayscale-dark font-suisse-intl underline"
                onClick={() =>
                  trackEvent(
                    click({
                      clickTarget: ClickTarget.AnchorLink,
                      clickText: '← Back to what you were reading',
                      clickLink: returnTo,
                    })
                  )
                }
              >
                ← Back to what you were reading
              </a>
            </div>
          )}
          {isSingleOffer ? (
            <SingleOffer description={description} offer={offers[0]} />
          ) : isMobileExperience ? (
            <MobileMultipleOffers defaultSelectedOffer={defaultSelectedOffer} offers={offers} />
          ) : (
            <MultipleOffers
              defaultSelectedOffer={defaultSelectedOffer}
              description={description}
              offers={offers}
            />
          )}
        </QueryClientProvider>
      </SubscribeFormProvider>
    </AppContextProvider>
  );
};

export default Subscribe;
